import type { Incident } from "../models/Incidents.ts";
import { ReporteChequeo } from "../models/ReporteChequeo.ts";
import { getIncidentRecordBranchId } from "./incidentBranchMeta.ts";
import type { BranchReportMeta } from "./payrollBranchMeta.ts";

/** Turnos normales (vespertino, etc.): salida hasta las 03:00 del día siguiente. */
export const STANDARD_SHIFT_CUTOFF_MINUTES = 3 * 60;

/** Turno RH «Nocturno»: solo esos colaboradores usan día operativo / corte de sucursal. */
export function isPartnerNocturnoShift(shiftName?: string | null): boolean {
  return String(shiftName || "").trim().toLowerCase() === "nocturno";
}

export function effectiveNightMetaForPartner(
  branchMeta: BranchReportMeta | undefined,
  shiftName?: string | null
): BranchReportMeta {
  const branchCutoff = branchMeta?.cutoffMinutes ?? STANDARD_SHIFT_CUTOFF_MINUTES;
  const enabled =
    Boolean(branchMeta?.nightScheduleEnabled) &&
    isPartnerNocturnoShift(shiftName);
  return {
    nightScheduleEnabled: enabled,
    cutoffMinutes: enabled ? branchCutoff : STANDARD_SHIFT_CUTOFF_MINUTES,
  };
}

export function partnerShiftFromIncidentSchedule(schedule?: {
  shift?: string;
  partner?: { candidate?: { shift?: { name?: string } } };
}): string | undefined {
  return (
    schedule?.partner?.candidate?.shift?.name ??
    schedule?.shift ??
    undefined
  );
}

/** Turno RH desde checadas del periodo (nómina / gráfica de días). */
export function partnerShiftFromCheckRecords(
  records: ReporteChequeo[] | undefined | null,
  partnerId: number
): string | undefined {
  const row = (records || []).find(
    (r) =>
      Number(r.partner_id) === Number(partnerId) &&
      String(r.candidate_shift_name || "").trim()
  );
  return row?.candidate_shift_name ?? undefined;
}

/** Nocturno efectivo en nómina: sucursal del colaborador + turno «Nocturno». */
export function effectiveNightMetaForPayrollPartner(
  payrollBranchMeta: Record<number, BranchReportMeta> | undefined,
  branchId: number,
  shiftName?: string | null
): BranchReportMeta {
  const base = payrollBranchMeta?.[branchId];
  return effectiveNightMetaForPartner(base, shiftName);
}

export function partnerUsesNightScheduleInPayroll(
  payrollBranchMeta: Record<number, BranchReportMeta> | undefined,
  branchId: number,
  shiftName?: string | null
): boolean {
  return effectiveNightMetaForPayrollPartner(
    payrollBranchMeta,
    branchId,
    shiftName
  ).nightScheduleEnabled;
}

export function resolvePayrollPartnerNightScheduleEnabled(options: {
  partnerId?: number;
  branchId?: number;
  realCheckRecords?: ReporteChequeo[];
  payrollBranchMeta?: Record<number, BranchReportMeta>;
}): boolean {
  const partnerId = Number(options.partnerId);
  const branchId = Number(options.branchId);
  if (!partnerId || !branchId || !options.payrollBranchMeta) {
    return false;
  }
  const shift = partnerShiftFromCheckRecords(
    options.realCheckRecords,
    partnerId
  );
  return partnerUsesNightScheduleInPayroll(
    options.payrollBranchMeta,
    branchId,
    shift
  );
}

/** Mayor corte operativo entre sucursales con horario nocturno cargado. */
export function maxNightCutoffAmongBranches(
  branchMetaMap: Record<number, BranchReportMeta>
): number {
  const cutoffs = Object.values(branchMetaMap || {})
    .filter((meta) => meta.nightScheduleEnabled)
    .map((meta) => meta.cutoffMinutes);
  return cutoffs.length > 0 ? Math.max(...cutoffs) : 3 * 60;
}

/** Algún colaborador visible usa pareo nocturno (sucursal + turno Nocturno). */
export function anyIncidentUsesNightSchedule(
  records: Incident[],
  branchMetaMap: Record<number, BranchReportMeta>
): boolean {
  return (records || []).some((record) => {
    const branchId = getIncidentRecordBranchId(record);
    const meta = effectiveNightMetaForPartner(
      branchMetaMap[branchId],
      partnerShiftFromIncidentSchedule(record?.schedule)
    );
    return meta.nightScheduleEnabled;
  });
}

/**
 * Vista por sucursal: ocultar columnas de horario planeado solo si la sucursal
 * filtrada tiene nocturno y hay al menos un colaborador Nocturno en la tabla.
 */
export function shouldHidePlannedColumnsForBranchIncidents(
  branchMeta: BranchReportMeta | undefined,
  records: Incident[]
): boolean {
  if (!branchMeta?.nightScheduleEnabled) {
    return false;
  }
  return (records || []).some((record) =>
    isPartnerNocturnoShift(partnerShiftFromIncidentSchedule(record?.schedule))
  );
}

export type IncidentsNightTableContext = {
  hidePlannedColumns: boolean;
  hasNocturnoPartners: boolean;
  maxNightCutoffMinutes: number;
};

export function resolveIncidentsNightTableContext(
  records: Incident[],
  branchMetaMap: Record<number, BranchReportMeta>,
  mode: "branch" | "directBoss",
  filterBranchId?: number
): IncidentsNightTableContext {
  const hasNocturnoPartners = (records || []).some((record) =>
    isPartnerNocturnoShift(partnerShiftFromIncidentSchedule(record?.schedule))
  );
  const maxNightCutoffMinutes = maxNightCutoffAmongBranches(branchMetaMap);

  if (mode === "branch") {
    const branchMeta =
      filterBranchId && filterBranchId > 0
        ? branchMetaMap[filterBranchId]
        : undefined;
    return {
      hidePlannedColumns: shouldHidePlannedColumnsForBranchIncidents(
        branchMeta,
        records
      ),
      hasNocturnoPartners,
      maxNightCutoffMinutes,
    };
  }

  return {
    hidePlannedColumns: anyIncidentUsesNightSchedule(records, branchMetaMap),
    hasNocturnoPartners,
    maxNightCutoffMinutes,
  };
}
